import { useId, useRef, useState } from 'react';
import { DATE_RANGE_PLACEHOLDER, formatDateRangeInput } from '../../utils/dateFormat';
import CalendarIcon from './CalendarIcon';
import CalendarPopoverPortal from './CalendarPopoverPortal';
import DualMonthRangeCalendar from './DualMonthRangeCalendar';
import { useCalendarPopover } from './useCalendarPopover';

type FormattedDateRangeInputProps = {
  id?: string;
  className?: string;
  from: string;
  to: string;
  onChange: (from: string, to: string) => void;
  placeholder?: string;
  disabled?: boolean;
  clearable?: boolean;
  onClear?: () => void;
  minDate?: string;
  maxDate?: string;
  enableYmdViews?: boolean;
};

/** Blocks keyboard typing while keeping Tab / arrow navigation usable. */
function blockManualDateEntry(e: React.KeyboardEvent<HTMLInputElement>) {
  if (e.key === 'Tab' || e.key.startsWith('Arrow') || e.key === 'Escape') return;
  if (e.key === 'Enter' || e.key === ' ') return;
  if (e.key.length === 1 || e.key === 'Backspace' || e.key === 'Delete') {
    e.preventDefault();
  }
}

export default function FormattedDateRangeInput({
  id,
  className = 'ccr-inp',
  from,
  to,
  onChange,
  placeholder = DATE_RANGE_PLACEHOLDER,
  disabled,
  clearable = true,
  onClear,
  minDate,
  maxDate,
  enableYmdViews = false,
}: FormattedDateRangeInputProps) {
  const popoverId = useId();
  const wrapRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);

  const display = formatDateRangeInput(from, to);

  useCalendarPopover(open, wrapRef, popoverRef, () => setOpen(false));

  const openCalendar = () => {
    if (disabled) return;
    setOpen(true);
  };

  const handleRangeChange = (nextFrom: string, nextTo: string) => {
    onChange(nextFrom, nextTo);
    if (nextFrom && nextTo) {
      setOpen(false);
    }
  };

  const showClear = clearable && Boolean(from || to) && !disabled;

  const handleClear = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) return;
    if (onClear) onClear();
    else onChange('', '');
    setOpen(false);
  };

  const inputClass = `${className} cal-field-inp date-range-inp`;

  return (
    <div className={`cal-field date-range-field${showClear ? ' has-clear' : ''}`} ref={wrapRef}>
      <input
        id={id}
        className={inputClass}
        type="text"
        readOnly
        autoComplete="off"
        disabled={disabled}
        placeholder={placeholder}
        value={display}
        onClick={openCalendar}
        onFocus={openCalendar}
        onPaste={(e) => e.preventDefault()}
        onKeyDown={(e) => {
          blockManualDateEntry(e);
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            openCalendar();
          }
        }}
        aria-expanded={open}
        aria-controls={open ? popoverId : undefined}
      />
      {showClear ? (
        <button
          type="button"
          className="cal-field-clear"
          aria-label="Clear date range"
          tabIndex={-1}
          onMouseDown={(e) => e.preventDefault()}
          onClick={handleClear}
        >
          ×
        </button>
      ) : null}
      <button
        type="button"
        className="cal-field-btn date-range-cal"
        disabled={disabled}
        aria-label="Open date range picker"
        aria-expanded={open}
        aria-controls={popoverId}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => setOpen((v) => !v)}
      >
        <CalendarIcon />
      </button>

      <CalendarPopoverPortal
        open={open}
        anchorRef={wrapRef}
        popoverRef={popoverRef}
        id={popoverId}
        className="cal-popover cal-popover--dual"
        ariaLabel="Select date range"
        estimatedHeight={enableYmdViews ? 380 : 320}
      >
        <DualMonthRangeCalendar
          from={from}
          to={to}
          onChange={handleRangeChange}
          minDate={minDate}
          maxDate={maxDate}
          enableYmdViews={enableYmdViews}
        />
      </CalendarPopoverPortal>
    </div>
  );
}
